import ClassFormEnrollmentSection from '@/components/ClassFormEnrollmentSection';
import ClassFormGeneralSettingsSection from '@/components/ClassFormGeneralSettingsSection';
import ClassFormHeader from '@/components/ClassFormHeader';
import ClassPeriod from '@/components/ClassPeriod';
import GoBackMenu from '@/components/GoBackNavbar';
import ImportHistory from '@/components/ImportHistory';
import Loader from '@/components/Loader';
import WorkShopMeeting from '@/components/WorkshopMeeting';
import WorkshopTips from '@/components/WorkshopTips';
import WorkshopType from '@/components/WorkshopType';
import { useProgramClass } from '@/hooks/useProgramClass';
import List from '@/models/List';
import { ProgramClass } from '@/models/ProgramClass';
import { Workshop, WorkshopTip } from '@/models/Workshop';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import { getNewDate } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { deleteProgramClassRequest, updateProgramClassRequest } from '@/store/programClass/actions';
import { LocaleData } from '@/translations/LocaleData';
import { format } from 'date-fns';
import moment from 'moment';
import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import FindPersonModal from '../FindPersonModal';
import { formInitialState, ProgramClassForm, programClassSchema } from './formConfig';
import './style.scss';

interface Props {
    entity: List;
}

const formatDateToString = (date: Date): string => {
    return `${format(date, 'yyyy-MM-dd')}T00:00:00.000Z`;
};

const WorkshopClassForm: React.FC<Props> = ({ entity }) => {
    const { id, classId } = useParams<{ id: string; classId: string }>();

    const { programClass, isLoading, revalidateResult, mutate, deleteProgramClassKey } = useProgramClass({
        programClassId: Number(classId),
        withEnrollmentsCount: true,
        withWorkshopContent: true,
    });

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);
    const applicationId: number = useSelector((state: ApplicationState) => state.identity.data.user.applicationId);
    const orgId: number = useSelector((state: ApplicationState) => state.identity.data.user.orgId);

    const [formState, setFormState] = useState({ ...formInitialState });
    const [formStateBkp, setFormStateBkp] = useState({ ...formInitialState });
    const { name, startDate, finishDate, enrollmentsCount, enableCourseCertificate, mandatory, workshops }: ProgramClassForm = formState;
    const [showFindPersonModal, setShowFindPersonModal] = useState<boolean>(false);
    const [showOnlyEnrolledInModal, setShowOnlyEnrolledInModal] = useState<boolean>(false);
    const [deleteAllowed, setDeleteAllowed] = useState<boolean>(false);
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [totalEnrollmentsCount, setTotalEnrollmentsCount] = useState<number>(enrollmentsCount);

    const workShopInitialState: Workshop = {
        id: undefined,
        programClass: { id: Number(classId) } as ProgramClass,
        course: { id: Number(id) } as List,
        tips: [] as WorkshopTip[],
        address: '',
        host: '',
        meetings: [] as WorkshopMeeting[],
    } as Workshop;

    const workshop: Workshop = (workshops && workshops[0]) || workShopInitialState;

    const dispatch = useDispatch();

    const history = useHistory();

    const initializeFormState = (editingProgramClass: ProgramClass) => {
        const auxStartDate: Date | undefined = editingProgramClass.startDate ? editingProgramClass.startDate : editingProgramClass.scheduledStartDate;

        handleOnChange('name', editingProgramClass.name, true);
        handleOnChange('startDate', auxStartDate ? getNewDate(auxStartDate) : undefined, true);
        handleOnChange('finishDate', editingProgramClass.finishDate ? getNewDate(editingProgramClass.finishDate) : undefined, true);
        handleOnChange('enrollmentsCount', editingProgramClass.enrollmentsCount, true);
        handleOnChange('enableCourseCertificate', editingProgramClass.hasCourseCertification, true);
        handleOnChange('mandatory', editingProgramClass.mandatory, true);

        if (editingProgramClass.workshops) {
            handleOnChange('workshops', [...editingProgramClass.workshops], true);
        }
    };

    const handleOnChange = (
        fieldName: string,
        value: string | Date | number | boolean | Workshop[] | WorkshopMeeting[] | undefined,
        isBkp: boolean = false,
    ) => {
        setFormState((prevState: any) => ({ ...prevState, [fieldName]: value }));
        if (isBkp) {
            if (fieldName === 'workshops') {
                setFormStateBkp((prevState: any) => ({ ...prevState, workshops: JSON.parse(JSON.stringify(value)) }));
            } else {
                setFormStateBkp((prevState: any) => ({ ...prevState, [fieldName]: value }));
            }
        }
    };

    const onSaveClickHandler = () => {
        if (programClass) {
            const scheduledStartDateAsString: string | null = !programClass.startDate
                ? startDate
                    ? formatDateToString(startDate as Date)
                    : null
                : null;

            const programClassToUpdate: ProgramClass = {
                ...programClass,
                name,
                startDate,
                scheduledStartDateAsString,
                scheduledStartDate: programClass.scheduledStartDate,
                finishDateAsString: finishDate ? moment(finishDate).format('YYYY-MM-DD') : null,
                finishDate,
                mandatory,
                workshops,
                hasCourseCertification: enableCourseCertificate,
            } as ProgramClass;

            dispatch(updateProgramClassRequest(programClassToUpdate));
            mutate(programClassToUpdate, false);
            handleWaitAndReturn();
        }
    };

    const onDeleteClickHandler = () => {
        dispatch(deleteProgramClassRequest(Number(classId)));

        handleWaitAndReturn();
    };

    const onValidateFormChange = (): boolean => {
        return !_.isEqual(formStateBkp, formState);
    };

    const handleWaitAndReturn = () => {
        setTimeout(() => {
            history.goBack();
        }, 1000);
    };

    useEffect(() => {
        if (!!programClass) {
            initializeFormState(programClass);
        }
    }, [programClass?.id]);

    useEffect(() => {
        if (!!programClass) {
            setTotalEnrollmentsCount(programClass.enrollmentsCount || 0);
        }
    }, [programClass?.enrollmentsCount]);

    useEffect(() => {
        if (!!programClass) {
            setDeleteAllowed(!programClass.startDate && totalEnrollmentsCount === 0);
        }
    }, [programClass?.startDate, totalEnrollmentsCount]);

    useEffect(() => {
        if (entity.id) {
            programClassSchema
                .validate(formState, { context: { workshopClass: true } })
                .then(() => setFormIsValid(true))
                .catch(() => setFormIsValid(false));
        }
    }, [entity, formState]);

    useEffect(() => {
        return () => {
            deleteProgramClassKey();
        };
    }, []);

    return (
        <div className="workshop-class-form">
            <GoBackMenu confirmable={onValidateFormChange()} />

            {programClass && (
                <FindPersonModal
                    enrollmentsCount={totalEnrollmentsCount}
                    onlyEnrolled={showOnlyEnrolledInModal}
                    programClassId={programClass.id}
                    setShow={setShowFindPersonModal}
                    setTotalEnrollments={setTotalEnrollmentsCount}
                    show={showFindPersonModal}
                />
            )}
            <div className="class-form">
                <div className="class-header">
                    <span>{strings.MANAGE_CLASSES}</span>
                    <div className="container-description">
                        <span className="item-type">{strings.WORKSHOP}</span>
                        <h4>{entity.name}</h4>
                    </div>
                </div>

                <div className="class-box-container">
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <ClassFormHeader
                                deleteAllowed={deleteAllowed}
                                formIsInvalid={!formIsValid || _.isEqual(formStateBkp, formState)}
                                handleOnChange={handleOnChange}
                                name={name}
                                onCancelClickHandler={() => history.goBack()}
                                onDeleteClickHandler={onDeleteClickHandler}
                                onSaveClickHandler={onSaveClickHandler}
                            />

                            <div className="class-form-container">
                                <section className="input-section">
                                    <WorkshopType
                                        onChange={(isOnline, address) =>
                                            handleOnChange('workshops', [
                                                { ...workshop, isOnline: isOnline !== undefined ? isOnline : workshop.isOnline, address },
                                            ])
                                        }
                                    />
                                </section>

                                <>
                                    <ClassFormGeneralSettingsSection
                                        enableCourseCertificate={enableCourseCertificate}
                                        handleOnChange={handleOnChange}
                                        mandatory={mandatory}
                                    />

                                    <section className="input-section">
                                        <ClassPeriod
                                            finishDateForm={finishDate}
                                            isWorkshop
                                            meetingsForm={workshop.meetings}
                                            onChange={({ startDate, finishDate }) => {
                                                handleOnChange('startDate', startDate);
                                                handleOnChange('finishDate', finishDate);
                                            }}
                                            startDateForm={startDate}
                                        />
                                    </section>
                                </>

                                <section className="input-section">
                                    <WorkShopMeeting
                                        finishDateForm={finishDate}
                                        onChange={(meetings: WorkshopMeeting[]) => handleOnChange('workshops', [{ ...workshop, meetings }])}
                                        startDateForm={startDate}
                                    />
                                </section>

                                <section className="input-section">
                                    <div className="container-workshop-instructor">
                                        <p>{strings.WORKSHOP_INFORMATION}</p>
                                        <span className="input-section-info">
                                            {strings.WORKSHOP_INSTRUCTOR}
                                            <span>*</span>
                                        </span>
                                        <input
                                            className="form-control"
                                            maxLength={100}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleOnChange('workshops', [{ ...workshop, host: e.target.value }])
                                            }
                                            placeholder={strings.WORKSHOP_INSTRUCTOR_PLACEHOLDER}
                                            required
                                            type="text"
                                            value={workshop.host}
                                        />
                                    </div>
                                </section>

                                <section className="input-section">
                                    <WorkshopTips onChange={(tips: WorkshopTip[]) => handleOnChange('workshops', [{ ...workshop, tips }])} />
                                </section>

                                <ClassFormEnrollmentSection
                                    applicationId={applicationId}
                                    organizationId={orgId}
                                    programClassId={Number(classId)}
                                    setShowFindPersonModal={setShowFindPersonModal}
                                    setShowOnlyEnrolledInModal={setShowOnlyEnrolledInModal}
                                    totalEnrollmentsCount={totalEnrollmentsCount}
                                />

                                <div className="input-section">
                                    {programClass && <ImportHistory onRefresh={revalidateResult} programClass={programClass} />}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default memo(WorkshopClassForm);
